import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { getTokenCookie, setTokenCookie } from '@root/utils/cookies/cookies';
import { Mutex } from 'async-mutex';
import { SCRIBE_API_URL } from 'utils/apiUrl';

import { signOut } from '../utils/api';
import { isCurrentUrlWhiteListed } from '../utils/urls';

const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
  baseUrl: `${SCRIBE_API_URL}`,
  prepareHeaders: (headers, { getState }) => {
    headers.set('X-Product', 'browser');
    const { token } = getTokenCookie();

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    } else if (process.env.NODE_ENV !== 'production') {
      console.warn('No token found for authenticated request.');
    }
    return headers;
  }
});

interface TokenResponseData {
  token: string;
  refresh: string;
}

const customFetchBase: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  // Wait until the mutex is available without locking it.
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        const { refresh } = getTokenCookie();
        const refreshResult = await baseQuery(
          {
            url: '/tokens/refresh/',
            method: 'POST',
            body: {
              refresh
            }
          },
          api,
          extraOptions
        );

        if (refreshResult.data) {
          const tokenResponse = refreshResult.data as TokenResponseData;

          setTokenCookie({ access: tokenResponse.token, refresh: tokenResponse.refresh });
          // Retry now that we have a functional token.
          result = await baseQuery(args, api, extraOptions);
        } else {
          const shouldNavigateToSignInPage = !isCurrentUrlWhiteListed();

          signOut(shouldNavigateToSignInPage);
        }
      } finally {
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};

export default customFetchBase;

export const baseApi = createApi({
  tagTypes: ['AppNotifications', 'DocumentChangeLogs', 'UnviewedChanges', 'Profile', 'DocumentViewSummary'],
  reducerPath: 'baseApi',
  baseQuery: customFetchBase,
  endpoints: () => ({})
});
